/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { useState, useRef, useEffect } from 'react';
import { Socket } from 'socket.io-client';

import { Card, CardContent } from '../ui/card';
import { Separator } from '../ui/separator';

import { ChatHeader } from './ChatHeader';
import { ChatHistory } from './ChatHistory';
import { ChatInput } from './ChatInput';
import { ChatMessageComponent } from './ChatMessage';
import { EmptyState } from './EmptyState';
import { LoadingMessage } from './LoadingMessage';

import { CodeAgentChatHistory, CodeAgentChatMessage } from '@/commons/data';

interface CodeAgentChatProps {
    /** Socket connection for communicating with the live agent */
    socket?: Socket;
    /** Height of the chat container */
    height?: string;
    /** Chat history from session state */
    codeAgentHistory?: CodeAgentChatHistory[];
    /** Callback to update chat history in session state */
    onChatHistoryChange?: (codeAgentHistory: CodeAgentChatHistory[]) => void;
    /** Whether the chat should be disabled (e.g., when session is not ready) */
    disabled?: boolean;
}

export const CodeAgentChat = ({
    socket,
    height = '100%',
    codeAgentHistory: initialChatHistory,
    onChatHistoryChange,
    disabled = false,
}: CodeAgentChatProps) => {
    const [inputValue, setInputValue] = useState('');
    const [codeAgentHistory, setcodeAgentHistory] = useState<CodeAgentChatHistory | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const [savedChats, setSavedChats] = useState<CodeAgentChatHistory[]>(initialChatHistory || []);
    const [currentChatId, setCurrentChatId] = useState<string | null>(() =>
        initialChatHistory && initialChatHistory.length > 0 ? initialChatHistory[0].id : null,
    );
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [codeAgentHistory]);

    // Socket event handlers
    useEffect(() => {
        if (!socket) return;

        const handleCodeAgentResponse = (data: { session: CodeAgentChatHistory }) => {
            setIsLoading(false);

            // Preserve existing messages and append only new ones from backend
            setcodeAgentHistory((prev) => {
                if (!prev) {
                    return data.session;
                }

                // Keep existing messages (with ratings preserved) and append new messages
                const existingMessageCount = prev.messages.length;
                const newMessages = data.session.messages.slice(existingMessageCount);

                return {
                    ...data.session,
                    messages: [...prev.messages, ...newMessages],
                };
            });

            // Set the current chat ID from the backend response
            // This ensures we use the authoritative ID from the backend
            if (data.session.id && data.session.id !== currentChatId) {
                setCurrentChatId(data.session.id);
            }
        };

        socket.on('codeAgentResponse', handleCodeAgentResponse);

        return () => {
            socket.off('codeAgentResponse', handleCodeAgentResponse);
        };
    }, [socket]);

    // Save current chat when messages change
    useEffect(() => {
        if (codeAgentHistory && codeAgentHistory.messages.length > 0 && codeAgentHistory.id) {
            const firstUserMessage = codeAgentHistory.messages.find((msg) => msg.role === 'user');
            const title = firstUserMessage?.content
                ? firstUserMessage.content.slice(0, 50) + (firstUserMessage.content.length > 50 ? '...' : '')
                : 'New Chat';
            const timestamps = codeAgentHistory.messages.map((msg) => msg.timestamp || 0).filter((t) => t > 0);
            const lastMessageDate = timestamps.length > 0 ? Math.max(...timestamps) : Date.now();

            // Check if this chat already exists in savedChats
            const existingChatIndex = savedChats.findIndex((chat) => chat.id === codeAgentHistory.id);

            if (existingChatIndex >= 0) {
                // Update existing chat
                setSavedChats((prev) =>
                    prev.map((chat) =>
                        chat.id === codeAgentHistory.id
                            ? {
                                  ...chat,
                                  title,
                                  lastMessageDate,
                                  messages: codeAgentHistory.messages,
                                  toolResults: codeAgentHistory.toolResults,
                              }
                            : chat,
                    ),
                );
            } else {
                // Create new chat - this happens when we get the first response from backend
                const newChat: CodeAgentChatHistory = {
                    id: codeAgentHistory.id,
                    finalCode: codeAgentHistory.finalCode,
                    title,
                    lastMessageDate,
                    messages: codeAgentHistory.messages,
                    toolResults: codeAgentHistory.toolResults,
                };
                setSavedChats((prev) => [newChat, ...prev]);
            }
        }
    }, [codeAgentHistory]);

    // Sync savedChats with parent component
    useEffect(() => {
        if (onChatHistoryChange) {
            onChatHistoryChange(savedChats);
        }
    }, [savedChats]);

    const handleSendMessage = () => {
        if (!inputValue.trim() || isLoading || !socket || disabled) return;

        const timestamp = Date.now();
        const userMessage: CodeAgentChatMessage = {
            role: 'user',
            content: inputValue.trim(),
            code: null,
            error: null,
            timestamp,
        };

        // Add user message to chat history
        setcodeAgentHistory((prev) => {
            if (prev) {
                return { ...prev, messages: [...prev.messages, userMessage] };
            }
            // For new chats, create a temporary session without an ID
            // The backend will provide the authoritative ID in the response
            return {
                id: '', // Temporary empty ID - will be replaced by backend response
                finalCode: '',
                title: 'New Chat',
                lastMessageDate: timestamp,
                messages: [userMessage],
                toolResults: {},
            };
        });

        // Send request to live agent
        socket.emit('codeAgentRequest', {
            prompt: inputValue.trim(),
        });

        setIsLoading(true);
        setInputValue('');
    };

    const handleCopyCode = async (code: string, messageIndex: number) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedMessageIndex(messageIndex);
            setTimeout(() => setCopiedMessageIndex(null), 2000);
        } catch {}
    };

    const handleRateMessage = (messageIndex: number, rating: 'thumbs_up' | 'thumbs_down' | null) => {
        setcodeAgentHistory((prev) =>
            prev
                ? {
                      ...prev,
                      messages: prev.messages.map((message, index) =>
                          index === messageIndex ? { ...message, rating } : message,
                      ),
                  }
                : null,
        );
    };

    const handleNewChat = () => {
        setcodeAgentHistory(null);
        setCurrentChatId(null);
        setShowHistory(false);

        // Send request to live agent
        socket?.emit('codeAgentSetSession', {
            session: null,
        });
    };

    const handleLoadChat = (chat: CodeAgentChatHistory) => {
        setcodeAgentHistory(chat);
        setCurrentChatId(chat.id);
        setShowHistory(false);

        // Send request to live agent
        socket?.emit('codeAgentSetSession', {
            session: chat,
        });
    };

    const handleDeleteChat = (chatId: string) => {
        setSavedChats((prev) => prev.filter((chat) => chat.id !== chatId));
        if (currentChatId === chatId) {
            handleNewChat();
        }
    };

    return (
        <div className="flex h-full min-w-0">
            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col min-w-0">
                <Card className="flex-1 flex flex-col min-w-0" style={{ height }}>
                    <ChatHeader
                        onToggleHistory={() => setShowHistory(!showHistory)}
                        onNewChat={handleNewChat}
                        isHistoryActive={showHistory}
                        isLoading={isLoading}
                        disabled={disabled}
                    />

                    <CardContent className="flex-1 flex flex-col p-0 min-h-0 min-w-0">
                        {showHistory ? (
                            /* Chat History View */
                            <div className="flex-1 flex flex-col min-h-0 min-w-0">
                                <ChatHistory
                                    savedChats={savedChats}
                                    currentChatId={currentChatId}
                                    onLoadChat={handleLoadChat}
                                    onDeleteChat={handleDeleteChat}
                                />
                            </div>
                        ) : (
                            /* Messages View */
                            <div className="flex-1 flex flex-col min-h-0 min-w-0">
                                <div
                                    className={`flex-1 px-6 pb-4 space-y-4 min-h-0 min-w-0 ${
                                        (codeAgentHistory && codeAgentHistory.messages.length > 0) || isLoading
                                            ? 'overflow-y-auto'
                                            : 'overflow-hidden'
                                    }`}
                                >
                                    {!codeAgentHistory || codeAgentHistory.messages.length === 0 ? (
                                        <EmptyState />
                                    ) : (
                                        codeAgentHistory?.messages.map((message, index) => (
                                            <ChatMessageComponent
                                                key={index}
                                                message={message}
                                                messageIndex={index}
                                                copiedMessageIndex={copiedMessageIndex}
                                                onCopyCode={handleCopyCode}
                                                onRateMessage={handleRateMessage}
                                            />
                                        ))
                                    )}

                                    {isLoading && <LoadingMessage />}

                                    <div ref={messagesEndRef} />
                                </div>

                                <Separator />

                                {/* Input Area - Only visible in messages view */}
                                <ChatInput
                                    inputValue={inputValue}
                                    isLoading={isLoading}
                                    isConnected={!!socket && !disabled}
                                    onInputChange={setInputValue}
                                    onSendMessage={handleSendMessage}
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
